import React from "react";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import Box from "@material-ui/core/Box";
import TextField from "@material-ui/core/TextField";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import Autocomplete from "@material-ui/lab/Autocomplete";
import LoadableButton from "src/components/LoadableButton";
import { FEATURE_FLAGS } from "src/interfaces/systemState.interface";
import type { SupportStateUserDataSuccess } from "src/interfaces/supportState.interface";

import { updateUser } from "src/store/support/actions";

function Loading() {
  return (
    <Box display="flex" flexDirection="column" alignItems="center" p={4}>
      <CircularProgress size={35} />
      <Typography variant="h6" component="span">
        Loading...
      </Typography>
    </Box>
  );
}

function enabledFeatureFlagKeys(
  featureFlags?: Record<string, boolean>
): string[] {
  if (!featureFlags) return [];
  return Object.entries(featureFlags)
    .filter(([, enabled]) => enabled)
    .map(([key]) => key)
    .sort();
}

function toFeatureFlagsMap(keys: string[]): Record<string, boolean> {
  const map: Record<string, boolean> = {};
  for (const key of keys) {
    const flag = key.trim();
    if (flag) map[flag] = true;
  }
  return map;
}

function Form({
  username,
  user,
}: {
  username: string;
  user: SupportStateUserDataSuccess & { loading?: boolean; error?: string };
}) {
  const history = useHistory();
  const dispatch = useDispatch();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: user.email || "",
      emailVerified: !!user.emailVerified,
      disabled: !!user.disabled,
      featureFlags: enabledFeatureFlagKeys(user.featureFlags),
    },
    onSubmit: async (values) => {
      const shouldRedirect =
        username !== user.uid && user.email !== values.email;
      await dispatch(
        updateUser(username, {
          ...(!!values.email && { email: values.email }),
          emailVerified: values.emailVerified,
          disabled: values.disabled,
          featureFlags: toFeatureFlagsMap(values.featureFlags),
        })
      );
      if (shouldRedirect) history.push(`/support/user/${values.email}`);
    },
  });

  if (!user.uid && user.loading) return <Loading />;
  if (user.error) return <div>error</div>;

  const disabled = !!user.loading || formik.isSubmitting;

  return (
    <Box
      px={2}
      pt={4}
      component="form"
      {...({ onSubmit: formik.handleSubmit } as object)}
    >
      <Box pb={2}>
        <TextField
          label="User ID"
          fullWidth
          defaultValue={user.uid}
          InputProps={{
            readOnly: true,
          }}
          disabled={disabled}
        />
      </Box>
      <Box pb={2}>
        <TextField
          label="Last Sign In Time"
          fullWidth
          defaultValue={user.metadata?.lastSignInTime}
          InputProps={{
            readOnly: true,
          }}
          disabled={disabled}
        />
      </Box>
      <Box pb={2}>
        <TextField
          label="E-Mail"
          name="email"
          fullWidth
          value={formik.values.email}
          onChange={formik.handleChange}
          disabled={disabled}
        />
      </Box>
      <Box>
        <FormControlLabel
          control={
            <Checkbox
              color="secondary"
              name="emailVerified"
              checked={formik.values.emailVerified}
              onChange={formik.handleChange}
              disabled={disabled}
            />
          }
          label="E-Mail verified"
        />
      </Box>
      <Box>
        <FormControlLabel
          control={
            <Checkbox
              color="secondary"
              name="disabled"
              checked={formik.values.disabled}
              onChange={formik.handleChange}
              disabled={disabled}
            />
          }
          label="Disabled"
        />
      </Box>
      <Box pb={2} pt={1}>
        <Autocomplete
          multiple
          freeSolo
          filterSelectedOptions
          options={[...FEATURE_FLAGS]}
          value={formik.values.featureFlags}
          onChange={(_, value) => {
            formik.setFieldValue(
              "featureFlags",
              value
                .map((flag) => (typeof flag === "string" ? flag.trim() : ""))
                .filter(Boolean)
            );
          }}
          disabled={disabled}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                variant="outlined"
                label={option}
                size="small"
                {...getTagProps({ index })}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Feature flags"
              helperText="Select known flags or type a custom flag name and press Enter"
            />
          )}
        />
      </Box>
      <Box display="flex" justifyContent="flex-end">
        <LoadableButton
          type="submit"
          variant="contained"
          color="primary"
          loading={formik.isSubmitting}
          disabled={disabled || !formik.isValid}
        >
          Update
        </LoadableButton>
      </Box>
    </Box>
  );
}

export default Form;
